"use client"
import { useState, useEffect } from "react"
import UploadPdfForDigitisation from "./UploadPdfForDigitisation"
import RenderingLatexAndPdfOfDigitisedNotes from "./renderingLatexAndPdfOfDigitisedNotes"
import {
  FileText,
  Clock,
  CheckCircle,
  XCircle,
  Loader,
  AlertCircle,
  Calendar,
  RefreshCw,
  Trash2,
  Cog,
  BookOpen,
  X,
  Search,
  Filter,
  ChevronDown,
  FileUp,
  Eye,
  Grid3X3,
  List,
} from "lucide-react"
import { postAws } from "../postAws"

const DigitiseNotes = () => {
  const [digitisedNotes, setDigitisedNotes] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [openingPdf, setOpeningPdf] = useState({})
  const [showShareModal, setShowShareModal] = useState(false)
  const [shareBookData, setShareBookData] = useState({ name: "", author: "", noteId: "", s3Uri: "" })
  const [isSharing, setIsSharing] = useState(false)
  const [error, setError] = useState(null)
  const [searchTerm, setSearchTerm] = useState("")
  const [showFilterDropdown, setShowFilterDropdown] = useState(false)
  const [activeFilter, setActiveFilter] = useState("all")
  const [isPdfUploadModalOpen, setIsPdfUploadModalOpen] = useState(false)
  const [showRenderingComponent, setShowRenderingComponent] = useState(false)
  const [selectedNoteData, setSelectedNoteData] = useState(null)
  const [viewMode, setViewMode] = useState("card") // New state for view toggle

  const fetchDigitisedNotes = async () => {
    setIsLoading(true)
    setError(null)
    try {
      const teacherId = localStorage.getItem("teacherId")
      const classroomId = localStorage.getItem("classRoomId")
      if (!teacherId || !classroomId) {
        throw new Error("Missing required user data")
      }
      const payload = {
        request: "get-digital-notes",
        body: {
          teacherId,
          classroomId,
        },
      }
      const response = await postAws("https://api-dev.aurait.ai/DigitiseNotesFromPdf/getNotes", "POST", payload)
      if (response.statusCode === 200) {
        const responseBody = typeof response.body === "string" ? JSON.parse(response.body) : response.body
        setDigitisedNotes(responseBody.notes || [])
      } else {
        throw new Error("Failed to fetch digitised notes")
      }
    } catch (error) {
      console.error("Error fetching digitised notes:", error)
      setError("Failed to load digitised notes. Please try again.")
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchDigitisedNotes()
  }, [])

  useEffect(() => {
    window.switchToDigitisedNotes = () => {
      fetchDigitisedNotes()
    }
    return () => {
      delete window.switchToDigitisedNotes
    }
  }, [])

  // Helper function to check if note has available versions
  const hasAvailableVersions = (note) => {
    return note.latexPdfVersions && note.latexPdfVersions.length > 0
  }

  // Helper function to determine if we should show Open Book button
  const shouldShowOpenBook = (note) => {
    return (
      (note.processingResult?.presignedUrl && note.processingResult.presignedUrl !== "") ||
      (note.digitisedNotesUri && note.digitisedNotesUri !== "") ||
      hasAvailableVersions(note)
    )
  }

  // Helper function to determine if we should show error
  const shouldShowError = (note) => {
    return note.status?.toLowerCase() === "failed" && !shouldShowOpenBook(note) && note.errorMessage
  }

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case "creating":
        return <Clock className="text-yellow-500" size={20} />
      case "processing":
        return <Cog className="text-blue-500 animate-spin" size={20} />
      case "completed":
        return <CheckCircle className="text-green-500" size={20} />
      case "failed":
        return <XCircle className="text-red-500" size={20} />
      default:
        return <FileText className="text-gray-500" size={20} />
    }
  }

  const getStatusText = (status) => {
    switch (status?.toLowerCase()) {
      case "creating":
        return "Creating..."
      case "processing":
        return "Processing..."
      case "completed":
        return "Completed"
      case "failed":
        return "Failed"
      default:
        return "Unknown"
    }
  }

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "creating":
        return "bg-yellow-50 text-yellow-700 border-yellow-200"
      case "processing":
        return "bg-blue-50 text-blue-700 border-blue-200"
      case "completed":
        return "bg-green-50 text-green-700 border-green-200"
      case "failed":
        return "bg-red-50 text-red-700 border-red-200"
      default:
        return "bg-gray-50 text-gray-700 border-gray-200"
    }
  }

  const handleOpenPdf = (note) => {
    if (!shouldShowOpenBook(note)) {
      return
    }
    setOpeningPdf((prev) => ({ ...prev, [note._id]: true }))

    let pdfUriToOpen = note.processingResult?.presignedUrl // Default to presignedUrl from processingResult
    if (hasAvailableVersions(note)) {
      // If versions exist, prioritize the latest version's PDF URI
      const latestVersion = note.latexPdfVersions[note.latexPdfVersions.length - 1]
      pdfUriToOpen = latestVersion.pdfS3Uri
    } else if (note.digitisedNotesUri && note.digitisedNotesUri !== "") {
      // Fallback to digitisedNotesUri if no versions but a direct URI exists
      pdfUriToOpen = note.digitisedNotesUri
    }

    setSelectedNoteData({ ...note, effectivePdfUri: pdfUriToOpen })
    setShowRenderingComponent(true)
    setOpeningPdf((prev) => ({ ...prev, [note._id]: false })) // Reset loading state after setting
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString)
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date)
  }

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm("Are you sure you want to delete this digitised note? This action cannot be undone.")) {
      return
    }
    try {
      const payload = {
        request: "delete-digitise-note",
        body: {
          noteId: noteId,
        },
      }
      const response = await postAws("https://api-dev.aurait.ai/DigitiseNotesFromPdf/getNotes", "POST", payload)
      if (response.statusCode === 200) {
        // Show success message
        const successMessage = document.createElement("div")
        successMessage.className = "fixed top-4 right-4 bg-green-500 text-white px-4 py-2 rounded-lg shadow-lg z-50"
        successMessage.textContent = "Digitised note deleted successfully!"
        document.body.appendChild(successMessage)
        setTimeout(() => {
          if (document.body.contains(successMessage)) {
            document.body.removeChild(successMessage)
          }
        }, 3000)
        // Refresh the notes list
        fetchDigitisedNotes()
      } else {
        throw new Error("Failed to delete digitised note")
      }
    } catch (error) {
      console.error("Error deleting digitised note:", error)
      // Show error message
      const errorMessage = document.createElement("div")
      errorMessage.className = "fixed top-4 right-4 bg-red-500 text-white px-4 py-2 rounded-lg shadow-lg z-50"
      errorMessage.textContent = "Failed to delete digitised note. Please try again."
      document.body.appendChild(errorMessage)
      setTimeout(() => {
        if (document.body.contains(errorMessage)) {
          document.body.removeChild(errorMessage)
        }
      }, 3000)
    }
  }

  const handleShareAsBook = async () => {
    if (!shareBookData.name.trim() || !shareBookData.author.trim()) {
      alert("Please fill in both book name and author")
      return
    }
    setIsSharing(true)
    try {
      const organisationId = localStorage.getItem("organisationId")
      const classRoomId = localStorage.getItem("classRoomId")

      // Determine the correct S3 URI to share
      let s3UriToShare = shareBookData.s3Uri
      const noteToShare = digitisedNotes.find((n) => n._id === shareBookData.noteId)
      if (noteToShare) {
        if (hasAvailableVersions(noteToShare)) {
          const latestVersion = noteToShare.latexPdfVersions[noteToShare.latexPdfVersions.length - 1]
          s3UriToShare = latestVersion.pdfS3Uri
        } else if (noteToShare.processingResult?.digitisedPdfUri) {
          s3UriToShare = noteToShare.processingResult.digitisedPdfUri
        } else if (noteToShare.digitisedNotesUri) {
          s3UriToShare = noteToShare.digitisedNotesUri
        }
      }

      const payload = {
        request: "upload-Book",
        body: {
          fileTitle: shareBookData.name.trim(),
          authorName: shareBookData.author.trim(),
          fileSubject: "Digitised Notes", // You can make this dynamic if needed
          fileUrl: s3UriToShare.replace("s3://padhai-aurait-tech/", ""), // Ensure this is correct for the backend
          fileUri: s3UriToShare,
          organisationId: organisationId,
          classRoomId: classRoomId,
        },
      }
      const response = await postAws("https://api-dev.aurait.ai/library", "POST", payload)
      if (response.statusCode === 201) {
        // Show success message
        const successMessage = document.createElement("div")
        successMessage.className = "fixed top-4 right-4 bg-green-500 text-white px-4 py-2 rounded-lg shadow-lg z-50"
        successMessage.textContent = "Book shared to library successfully!"
        document.body.appendChild(successMessage)
        setTimeout(() => {
          if (document.body.contains(successMessage)) {
            document.body.removeChild(successMessage)
          }
        }, 3000)
        // Close modal and reset form
        setShowShareModal(false)
        setShareBookData({ name: "", author: "", noteId: "", s3Uri: "" })
      } else {
        throw new Error("Failed to share book to library")
      }
    } catch (error) {
      console.error("Error sharing book:", error)
      // Show error message
      const errorMessage = document.createElement("div")
      errorMessage.className = "fixed top-4 right-4 bg-red-500 text-white px-4 py-2 rounded-lg shadow-lg z-50"
      errorMessage.textContent = "Failed to share book to library. Please try again."
      document.body.appendChild(errorMessage)
      setTimeout(() => {
        if (document.body.contains(errorMessage)) {
          document.body.removeChild(errorMessage)
        }
      }, 3000)
    } finally {
      setIsSharing(false)
    }
  }

  const getFilteredNotes = () => {
    let filtered = digitisedNotes.filter(
      (note) =>
        note.noteTitle?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        note.fileName?.toLowerCase().includes(searchTerm.toLowerCase()),
    )
    if (activeFilter !== "all") {
      filtered = filtered.filter((note) => {
        const effectiveStatus = hasAvailableVersions(note) ? "completed" : note.status?.toLowerCase()
        return effectiveStatus === activeFilter.toLowerCase()
      })
    }
    return filtered
  }

  const filterOptions = [
    { id: "all", label: "All Notes", icon: <FileText size={16} /> },
    { id: "creating", label: "Creating", icon: <Clock size={16} /> },
    { id: "processing", label: "Processing", icon: <Cog size={16} /> },
    { id: "completed", label: "Completed", icon: <CheckCircle size={16} /> },
    { id: "failed", label: "Failed", icon: <XCircle size={16} /> },
  ]

  const getFilterLabel = () => {
    switch (activeFilter) {
      case "creating":
        return "Creating"
      case "processing":
        return "Processing"
      case "completed":
        return "Completed"
      case "failed":
        return "Failed"
      default:
        return "All"
    }
  }

  // Helper function to get effective status (prioritize versions over failed status)
  const getEffectiveStatus = (note) => {
    if (shouldShowOpenBook(note)) {
      return "completed"
    }
    return note.status
  }

  const renderNoteCard = (note) => (
    <div
      key={note._id}
      className={`bg-white rounded-xl p-5 border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300 ease-in-out flex flex-col ${
        shouldShowOpenBook(note) ? "cursor-pointer" : ""
      }`}
      onClick={() => shouldShowOpenBook(note) && handleOpenPdf(note)}
    >
      <div className="flex justify-between items-start mb-4">
        <div className="flex items-center gap-3">
          {getStatusIcon(getEffectiveStatus(note))}
          <div>
            <h3 className="text-lg font-bold text-gray-800 line-clamp-1">{note.noteTitle}</h3>
            <p className="text-sm text-gray-500 line-clamp-1">{note.fileName}</p>
          </div>
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation()
            handleDeleteNote(note._id)
          }}
          className="p-2 rounded-full text-gray-400 hover:text-red-500 hover:bg-gray-100 transition-colors"
          title="Delete digitised note"
        >
          <Trash2 size={18} />
        </button>
      </div>

      <div className="flex flex-col gap-1 text-xs text-gray-500 mb-4">
        <div className="flex items-center gap-1.5">
          <Calendar size={14} />
          <span>Created: {formatDate(note.createdAt)}</span>
        </div>
        {note.updatedAt !== note.createdAt && (
          <div className="flex items-center gap-1.5">
            <Calendar size={14} />
            <span>Updated: {formatDate(note.updatedAt)}</span>
          </div>
        )}
      </div>

      <div className="flex-grow mb-4">
        <div
          className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(
            getEffectiveStatus(note),
          )}`}
        >
          {getStatusText(getEffectiveStatus(note))}
        </div>

        {note.status?.toLowerCase() === "creating" && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mt-3">
            <div className="flex items-center gap-2">
              <Loader className="animate-spin text-yellow-600" size={16} />
              <span className="text-yellow-700 text-sm font-medium">
                Your PDF is being digitised. This may take a few minutes...
              </span>
            </div>
          </div>
        )}
        {note.status?.toLowerCase() === "processing" && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-3">
            <div className="flex items-center gap-2">
              <Cog className="animate-spin text-blue-600" size={16} />
              <span className="text-blue-700 text-sm font-medium">Processing your digitised PDF. Almost ready...</span>
            </div>
            <div className="mt-2">
              <div className="w-full bg-blue-200 rounded-full h-2">
                <div className="bg-blue-600 h-2 rounded-full animate-pulse" style={{ width: "75%" }}></div>
              </div>
            </div>
          </div>
        )}
        {shouldShowError(note) && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mt-3">
            <div className="flex items-start gap-2">
              <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={16} />
              <div>
                <span className="text-red-700 text-sm font-medium block">Digitisation failed</span>
                <span className="text-red-600 text-xs break-words">{note.errorMessage}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      <div
        className="flex justify-end gap-2 mt-auto pt-4 border-t border-gray-100"
        onClick={(e) => e.stopPropagation()}
      >
        {shouldShowOpenBook(note) && (
          <button
            onClick={(e) => {
              e.stopPropagation()
              handleOpenPdf(note)
            }}
            disabled={openingPdf[note._id]}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors text-sm"
          >
            {openingPdf[note._id] ? (
              <>
                <Loader className="animate-spin" size={16} />
                Opening...
              </>
            ) : (
              <>
                <Eye size={16} />
                Open Book
              </>
            )}
          </button>
        )}
        {shouldShowOpenBook(note) && (
          <button
            onClick={(e) => {
              e.stopPropagation()
              setShareBookData({
                name: note.noteTitle,
                author: "",
                noteId: note._id,
                s3Uri:
                  note.processingResult?.digitisedPdfUri ||
                  (hasAvailableVersions(note) ? note.latexPdfVersions[note.latexPdfVersions.length - 1].pdfS3Uri : ""),
              })
              setShowShareModal(true)
            }}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2 text-sm"
          >
            <BookOpen size={16} />
            Share as Book
          </button>
        )}
        {(note.status?.toLowerCase() === "creating" || note.status?.toLowerCase() === "processing") && (
          <button
            onClick={(e) => {
              e.stopPropagation()
              fetchDigitisedNotes()
            }}
            className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors flex items-center gap-2 text-sm"
          >
            <RefreshCw size={16} />
            Check Status
          </button>
        )}
      </div>
    </div>
  )

  const renderNoteRow = (note) => (
    <tr key={note._id} className="hover:bg-gray-50">
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center">
          <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-r from-indigo-600 to-blue-600 rounded-lg flex items-center justify-center mr-3">
            <FileText className="w-4 h-4 text-white" />
          </div>
          <div className="text-sm font-medium text-gray-900 max-w-xs truncate">{note.noteTitle}</div>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{note.fileName}</td>
      <td className="px-6 py-4 whitespace-nowrap">
        <span
          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(
            getEffectiveStatus(note),
          )}`}
        >
          {getStatusText(getEffectiveStatus(note))}
        </span>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        <div className="flex items-center">
          <Calendar size={14} className="mr-2 text-gray-400" />
          {formatDate(note.createdAt)}
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
        <div className="flex items-center justify-end gap-2">
          {shouldShowOpenBook(note) && (
            <button
              onClick={(e) => {
                e.stopPropagation()
                handleOpenPdf(note)
              }}
              disabled={openingPdf[note._id]}
              className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
            >
              {openingPdf[note._id] ? (
                <>
                  <Loader className="animate-spin mr-1" size={14} />
                  Opening...
                </>
              ) : (
                <>
                  <Eye size={14} className="mr-1" />
                  Open
                </>
              )}
            </button>
          )}
          {shouldShowOpenBook(note) && (
            <button
              onClick={(e) => {
                e.stopPropagation()
                setShareBookData({
                  name: note.noteTitle,
                  author: "",
                  noteId: note._id,
                  s3Uri:
                    note.processingResult?.digitisedPdfUri ||
                    (hasAvailableVersions(note)
                      ? note.latexPdfVersions[note.latexPdfVersions.length - 1].pdfS3Uri
                      : ""),
                })
                setShowShareModal(true)
              }}
              className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-colors duration-200"
            >
              <BookOpen size={14} className="mr-1" />
              Share
            </button>
          )}
          {(note.status?.toLowerCase() === "creating" || note.status?.toLowerCase() === "processing") && (
            <button
              onClick={(e) => {
                e.stopPropagation()
                fetchDigitisedNotes()
              }}
              className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-gray-500 hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400 transition-colors duration-200"
            >
              <RefreshCw size={14} className="mr-1" />
              Check Status
            </button>
          )}
          <button
            onClick={(e) => {
              e.stopPropagation()
              handleDeleteNote(note._id)
            }}
            className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors duration-200"
          >
            <Trash2 size={14} className="mr-1" />
            Delete
          </button>
        </div>
      </td>
    </tr>
  )

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="bg-white p-6 sm:p-8 rounded-xl shadow-md text-center max-w-md mx-auto">
          <Loader className="animate-spin text-blue-600 mx-auto mb-3 sm:mb-4" size={36} />
          <h2 className="text-lg sm:text-xl font-bold text-gray-800 mb-1 sm:mb-2">Loading digitised notes</h2>
          <p className="text-sm sm:text-base text-gray-600">Please wait while we fetch your digitised notes...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="bg-white p-6 sm:p-8 rounded-xl shadow-md text-center max-w-md mx-auto">
          <AlertCircle className="text-red-500 mx-auto mb-3 sm:mb-4" size={36} />
          <h2 className="text-lg sm:text-xl font-bold text-gray-800 mb-1 sm:mb-2">Something went wrong</h2>
          <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">{error}</p>
          <button
            onClick={fetchDigitisedNotes}
            className="px-4 sm:px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-blue-100">
      <div className="container mx-auto px-2 md:px-4 py-0 max-w-7xl">
        <div className="bg-white rounded-xl md:rounded-2xl shadow-md overflow-hidden mt-6 md:mt-8">
          <div className="bg-gradient-to-r from-indigo-500 to-blue-600 px-3 md:px-4 py-3 md:py-4">
            {/* Mobile view */}
            <div className="block lg:hidden">
              <div className="flex items-center gap-2">
                {/* View Toggle */}
                <div className="flex items-center bg-white/20 backdrop-blur-sm rounded-lg border border-white/30">
                  <button
                    onClick={() => setViewMode("card")}
                    className={`p-1.5 rounded-l-lg transition-colors ${
                      viewMode === "card" ? "bg-white/30 text-white" : "text-white/70 hover:text-white"
                    }`}
                  >
                    <Grid3X3 size={12} />
                  </button>
                  <button
                    onClick={() => setViewMode("list")}
                    className={`p-1.5 rounded-r-lg transition-colors ${
                      viewMode === "list" ? "bg-white/30 text-white" : "text-white/70 hover:text-white"
                    }`}
                  >
                    <List size={12} />
                  </button>
                </div>
                <div className="relative flex-1">
                  <input
                    type="text"
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full px-3 py-1.5 pl-7 border border-white/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-white/50 transition-all duration-300 ease-in-out bg-white/10 backdrop-blur-sm text-white placeholder-white/70 text-xs"
                  />
                  <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-white/70" size={12} />
                </div>
                <div className="relative">
                  <button
                    onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                    className="flex items-center gap-1 px-2 py-1.5 rounded-lg bg-white/20 backdrop-blur-sm text-white text-xs font-medium border border-white/30"
                  >
                    <Filter size={12} />
                    <ChevronDown
                      size={10}
                      className={`transition-transform ${showFilterDropdown ? "rotate-180" : ""}`}
                    />
                  </button>
                  {showFilterDropdown && (
                    <div className="absolute right-0 mt-1 w-32 bg-white rounded-md shadow-lg z-10 border border-gray-200 py-1 animate-scale-in">
                      {filterOptions.map(({ id, label, icon }) => (
                        <button
                          key={id}
                          onClick={() => {
                            setActiveFilter(id)
                            setShowFilterDropdown(false)
                          }}
                          className={`w-full px-2 py-1.5 text-xs text-left flex items-center transition-colors hover:bg-gray-50 ${
                            activeFilter === id ? "bg-indigo-50 text-indigo-700" : "text-gray-700"
                          }`}
                        >
                          <span className="mr-1.5">{icon}</span>
                          {label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <button
                  onClick={() => setIsPdfUploadModalOpen(true)}
                  className="bg-white/20 backdrop-blur-sm text-white px-2 py-1.5 rounded-lg hover:bg-white/30 transition-colors duration-300 flex items-center gap-1 border border-white/30 text-xs font-medium"
                >
                  <FileUp size={12} />
                  <span>PDF</span>
                </button>
              </div>
            </div>
            {/* Desktop view */}
<div className="hidden lg:flex lg:items-center justify-between gap-4">
  {/* View Toggle - Left side */}
  <div className="flex items-center bg-white/20 backdrop-blur-sm rounded-lg border border-white/30">
    <button
      onClick={() => setViewMode("card")}
      className={`flex items-center gap-1 p-2 rounded-l-lg transition-colors text-xs ${
        viewMode === "card" ? "bg-white/30 text-white" : "text-white/70 hover:text-white"
      }`}
      title="Card View"
    >
      <Grid3X3 size={14} />
      <span>Card</span>
    </button>
    <button
      onClick={() => setViewMode("list")}
      className={`flex items-center gap-1 p-2 rounded-r-lg transition-colors text-xs ${
        viewMode === "list" ? "bg-white/30 text-white" : "text-white/70 hover:text-white"
      }`}
      title="List View"
    >
      <List size={14} />
      <span>List</span>
    </button>
  </div>

  {/* Search + Filter + Upload - Right side */}
  <div className="flex items-center gap-4">
    {/* Search */}
    <div className="relative">
      <input
        type="text"
        placeholder="Search digitised notes"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        className="w-64 px-4 py-2 pl-10 border border-white/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-white/50 transition-all duration-300 ease-in-out bg-white/10 backdrop-blur-sm text-white placeholder-white/70 text-sm"
      />
      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/70" size={14} />
    </div>

    {/* Filter */}
    <div className="relative">
      <button
        onClick={() => setShowFilterDropdown(!showFilterDropdown)}
        className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/20 backdrop-blur-sm text-white text-sm font-medium border border-white/30 hover:bg-white/30 transition-colors"
      >
        <Filter size={14} />
        <span>{getFilterLabel()}</span>
        <ChevronDown
          size={14}
          className={`transition-transform ${showFilterDropdown ? "rotate-180" : ""}`}
        />
      </button>
      {showFilterDropdown && (
        <div className="absolute right-0 mt-1 w-40 bg-white rounded-md shadow-lg z-10 border border-gray-200 py-1 animate-scale-in">
          {filterOptions.map(({ id, label, icon }) => (
            <button
              key={id}
              onClick={() => {
                setActiveFilter(id)
                setShowFilterDropdown(false)
              }}
              className={`w-full px-3 py-2 text-sm text-left flex items-center transition-colors hover:bg-gray-50 ${
                activeFilter === id ? "bg-indigo-50 text-indigo-700" : "text-gray-700"
              }`}
            >
              <span className="mr-2">{icon}</span>
              {label}
            </button>
          ))}
        </div>
      )}
    </div>

    {/* Upload Button */}
    <button
      onClick={() => setIsPdfUploadModalOpen(true)}
      className="bg-white/20 backdrop-blur-sm text-white px-5 py-2 rounded-lg hover:bg-white/30 transition-colors duration-300 flex items-center gap-2 border border-white/30 text-sm font-medium"
    >
      <FileUp size={16} />
      <span>Digitise PDF Notes</span>
    </button>
  </div>
</div>

          </div>
          <div className="p-3 md:p-6">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <Loader className="animate-spin text-blue-600 mx-auto mb-4" size={32} />
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">Loading digitised notes</h3>
                  <p className="text-gray-600">Please wait while we fetch your digitised notes...</p>
                </div>
              </div>
            ) : error ? (
              <div className="text-center py-12">
                <div className="bg-red-100 p-6 rounded-full inline-flex mb-4">
                  <AlertCircle className="text-red-500" size={32} />
                </div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Error loading digitised notes</h3>
                <p className="text-gray-600 mb-6">{error}</p>
                <button
                  onClick={fetchDigitisedNotes}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 mx-auto"
                >
                  <RefreshCw size={16} />
                  Try Again
                </button>
              </div>
            ) : getFilteredNotes().length === 0 ? (
              <div className="text-center py-12">
                <div className="bg-gray-100 p-6 rounded-full inline-flex mb-4">
                  <FileText className="text-gray-400" size={32} />
                </div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">
                  {searchTerm || activeFilter !== "all" ? "No matching notes found" : "No digitised notes found"}
                </h3>
                <p className="text-gray-600 mb-6">
                  {searchTerm || activeFilter !== "all"
                    ? "Try adjusting your search terms or filters."
                    : "You haven't digitised any PDF notes yet. Upload a PDF to get started!"}
                </p>
                <button
                  onClick={() => setIsPdfUploadModalOpen(true)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 mx-auto"
                >
                  <FileUp size={16} />
                  Digitise PDF Notes
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-bold text-gray-800">
                    Digitised PDF Notes
                    {(searchTerm || activeFilter !== "all") && (
                      <span className="text-sm font-normal text-gray-500 ml-2">
                        ({getFilteredNotes().length} of {digitisedNotes.length})
                      </span>
                    )}
                  </h2>
                  <button
                    onClick={fetchDigitisedNotes}
                    className="px-3 py-2 text-gray-600 hover:text-blue-600 hover:bg-gray-100 rounded-lg transition-colors flex items-center gap-2"
                  >
                    <RefreshCw size={16} />
                    Refresh
                  </button>
                </div>
                {viewMode === "card" ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-4">
                    {getFilteredNotes().map(renderNoteCard)}
                  </div>
                ) : (
                  <div className="bg-white rounded-lg shadow-md overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Note Title
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              File Name
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Status
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Created At
                            </th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {getFilteredNotes().map(renderNoteRow)}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      <UploadPdfForDigitisation isOpen={isPdfUploadModalOpen} onClose={() => setIsPdfUploadModalOpen(false)} />
      {/* Share as Book Modal */}
      {showShareModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-4 rounded-t-xl">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold text-white flex items-center">
                  <BookOpen className="mr-2" size={20} />
                  Share as Book in Library
                </h2>
                <button
                  onClick={() => {
                    setShowShareModal(false)
                    setShareBookData({ name: "", author: "", noteId: "", s3Uri: "" })
                  }}
                  className="text-white/80 hover:text-white p-1 rounded-full hover:bg-white/20 transition-colors"
                >
                  <X size={20} />
                </button>
              </div>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                <div>
                  <label htmlFor="book-name" className="block text-sm font-medium text-gray-700 mb-1">
                    Book Name
                  </label>
                  <input
                    type="text"
                    id="book-name"
                    value={shareBookData.name}
                    onChange={(e) => setShareBookData({ ...shareBookData, name: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="Enter book name"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="book-author" className="block text-sm font-medium text-gray-700 mb-1">
                    Author
                  </label>
                  <input
                    type="text"
                    id="book-author"
                    value={shareBookData.author}
                    onChange={(e) => setShareBookData({ ...shareBookData, author: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="Enter author name"
                    required
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowShareModal(false)
                    setShareBookData({ name: "", author: "", noteId: "", s3Uri: "" })
                  }}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleShareAsBook}
                  disabled={isSharing}
                  className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
                >
                  {isSharing ? (
                    <>
                      <Loader className="animate-spin" size={16} />
                      Sharing...
                    </>
                  ) : (
                    <>
                      <BookOpen size={16} />
                      Share as Book
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showRenderingComponent && selectedNoteData && (
        <RenderingLatexAndPdfOfDigitisedNotes
          noteData={selectedNoteData}
          onClose={() => {
            setShowRenderingComponent(false)
            setSelectedNoteData(null)
          }}
        />
      )}
    </div>
  )
}

export default DigitiseNotes
